<template>
    <Head title="Edit Award" />
    <BreezeAuthenticatedLayout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Update Award - {{ award.name }}
            </h2>
        </template>
        <BreadCrumbs :items="navItems"></BreadCrumbs>
        <div class="w-full my-6 overflow-hidden py-12 shadow">
            <div class="px-6 pt-4 pb-2">
                <form enctype="multipart/form-data" @submit.prevent="submit" class="w-full">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Name</admin-app-label>
                            <input-basic
                                placeholder="Name"
                                v-model="form.name"
                                :error="errors['name']"
                            />
                            <input-error v-if="errors['name']">
                                {{ errors.name }}
                            </input-error>
                        </div>
                        <div class="mb-6 md:mb-4 relative">
                            <admin-app-label>Group</admin-app-label>
                            <a class="block tracking-wider uppercase cursor-pointer mt-2 absolute -top-4 right-0" href="/admin/award-groups/create">
                                <font-awesome-icon icon="plus" class="mr-3"/>
                                Add New Group
                            </a>
                            <input-select
                                placeholder="Group"
                                labelKey="Group"
                                label="name"
                                :options="groups"
                                v-model="form.group_id"
                                :error="errors['group_id']"
                                :reduce="groups => groups.id"
                            />
                        </div>
                    </div>
                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Excerpt</admin-app-label>
                        <input-textarea
                            class="min-h-[200px]"
                            placeholder="Excerpt"
                            v-model="form.excerpt"
                            :error="errors['excerpt']"
                        />
                        <input-error v-if="errors['excerpt']">
                            {{ errors.excerpt }}
                        </input-error>
                        <p class="text-sm text-placeholder mt-1">
                            A small excerpt that will serve as a short description of this award.
                            This excerpt will be used on the awards page next to the winner.
                        </p>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Image</admin-app-label>
                            <image-upload
                                :key="`Image`"
                                :label="`Image`"
                                v-model="form.image"
                                :error="errors['image']"
                            />
                        </div>
                        <div v-if="award.path" class="mb-6 md:mb-4">
                            <admin-app-label>Current Image</admin-app-label>
                            <img class="object-contain p-3 border-solid border-2 border-grey-200 rounded"
                                 :src="award.path"
                                 :alt="award.path"/>
                        </div>
                    </div>
                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Order</admin-app-label>
                        <input-basic
                            type="number"
                            placeholder="Order"
                            v-model="form.order"
                            min="0"
                            :error="errors['order']"
                        />
                        <p class="text-sm text-placeholder mt-1">
                            This sets the order of the award in it's group. 0 being first.
                        </p>
                    </div>
                    <app-button type="submit" :disabled="form.processing" class="mt-8">Update</app-button>
                </form>
            </div>
        </div>
        <div v-if="award.products.length" class="relative w-full py-2 my-6 overflow-hidden shadow">
            <div class="px-2 md:px-6 pt-2 md:pt-4 pb-2">
                <h2 class="font-semibold text-xl leading-tight mb-4">
                    Tyres With Award - {{ award.name }}
                </h2>
                <div class="overflow-x-auto">
                    <div class="inline-block min-w-full overflow-hidden">
                        <table class="w-full table-auto md:table-fixed">
                            <thead class="uppercase text-gray-600 text-xs font-semibold bg-gray-200">
                            <tr class="hidden lg:table-row">
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">ID</th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">Name</th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">&nbsp;</th>
                            </tr>
                            </thead>
                            <tbody class="my-3 flex-1 lg:flex-none">
                            <tr v-if="award.products.length"
                                v-for="product of award.products"
                                class="mb-2 lg:mb-0 border-2 lg:border-b-2 border-grey-200 lg:border-t lg:first:border-t-0 flex p-1 md:p-3 hover:bg-gray-100 lg:table-row flex-col w-full flex-wrap">
                                <td class="p-1 lg:p-5 lg:border-l-2 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden">ID</label>
                                    {{ product.id }}
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden">Title</label>
                                    {{ product.name }}
                                </td>
                                <td class="px-5 py-5 lg:border-r lg:border-b border-grey-200 bg-white text-center lg:text-left text-sm md:text-base">
                                    <admin-link :href="route('admin.products.edit', product)">Edit</admin-link>
                                </td>
                            </tr>
                            <tr v-else>
                                <td class="py-6 bg-red" colspan="3" align="center">
                                    <p class="text-white font-bold">No products found</p>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>

<script>
import AddVideos from "@/Components/Admin/Forms/AddVideos";
import AppButton from "@/Components/AppButton";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import AdminLink from "@/Components/Admin/AdminLink";
import BreezeAuthenticatedLayout from "@/Layouts/Admin/Authenticated.vue";
import {Head} from "@inertiajs/inertia-vue3";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import InputTextarea from "@/Components/Admin/Forms/Inputs/InputTextarea";
import InputBasic from "@/Components/Admin/Forms/Inputs/InputBasic";
import ImageUpload from "@/Components/Admin/Forms/Inputs/ImageUpload";
import InputError from "@/Components/Forms/Inputs/InputError";
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";

export default {
    name: "Edit",
    components: {
        AppButton,
        AdminLink,
        AdminAppLabel,
        BreezeAuthenticatedLayout,
        BreadCrumbs,
        InputBasic,
        InputError,
        InputSelect,
        InputTextarea,
        ImageUpload,
        Head,
    },
    props: {
        award: Object,
        groups: Object,
        classifications: Object,
        errors: Object
    },
    data() {
        return {
            navItems: [
                {title: "Awards", path: "/admin/awards"},
            ],
            form: this.$inertia.form({
                ...this.award,
                image: "",
                _method: "put",
            }),
        }
    },
    mounted() {
        this.navItems.push({
            title: this.award.name,
            path: this.route("admin.awards.edit", this.award)
        });
    },
    methods: {
        submit()
        {
            this.form.post(`/admin/awards/${this.award.id}`);
        }
    }
}
</script>

<style scoped>

</style>
